/**
 * RECURRENCIA AVANZADA — FASE 6.2 §S
 *
 * Serializa/interpreta la RRULE canónica que entiende el motor real
 * (`backend/src/generador/recurrencia.ts`) a partir de un estado de
 * formulario sencillo — checkboxes de días, intervalo, posición mensual.
 * El usuario nunca ve ni escribe RRULE a mano.
 *
 * MISMA gramática que el backend, copiada a propósito (frontend y backend
 * son dos proyectos TS separados, sin paquete compartido — mismo criterio
 * que ya documenta `frecuencias.ts`): FREQ, INTERVAL, BYDAY, BYSETPOS,
 * mismos siete códigos de día (MO..SU) y mismas cinco posiciones (1,2,3,4,-1).
 * Si la gramática del motor cambia, este fichero cambia con ella.
 */

export type DiaSemana = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';

export const DIAS_SEMANA: ReadonlyArray<{ codigo: DiaSemana; corta: string; larga: string }> = [
  { codigo: 'MO', corta: 'L', larga: 'Lunes' },
  { codigo: 'TU', corta: 'M', larga: 'Martes' },
  { codigo: 'WE', corta: 'X', larga: 'Miércoles' },
  { codigo: 'TH', corta: 'J', larga: 'Jueves' },
  { codigo: 'FR', corta: 'V', larga: 'Viernes' },
  { codigo: 'SA', corta: 'S', larga: 'Sábado' },
  { codigo: 'SU', corta: 'D', larga: 'Domingo' },
];

export const POSICIONES_MENSUAL: ReadonlyArray<{ valor: number; etiqueta: string }> = [
  { valor: 1, etiqueta: 'Primer' },
  { valor: 2, etiqueta: 'Segundo' },
  { valor: 3, etiqueta: 'Tercer' },
  { valor: 4, etiqueta: 'Cuarto' },
  { valor: -1, etiqueta: 'Último' },
];

export type FrecuenciaBase = 'DIARIA' | 'SEMANAL' | 'MENSUAL' | 'ANUAL' | 'S.N.';
export type ModoMensual = 'DIA' | 'POSICION';

/** Estado editable del formulario — nunca se guarda tal cual, siempre se serializa a RRULE. */
export interface EstadoRecurrencia {
  readonly freq: FrecuenciaBase;
  readonly intervalo: number;
  readonly byday: readonly DiaSemana[];
  readonly modoMensual: ModoMensual;
  readonly posicionMensual: number;
  readonly diaSemanaMensual: DiaSemana;
}

export const ESTADO_INICIAL: EstadoRecurrencia = {
  freq: 'SEMANAL',
  intervalo: 1,
  byday: [],
  modoMensual: 'DIA',
  posicionMensual: 1,
  diaSemanaMensual: 'MO',
};

const FREQ_A_RFC: Readonly<Record<Exclude<FrecuenciaBase, 'S.N.'>, string>> = {
  DIARIA: 'DAILY', SEMANAL: 'WEEKLY', MENSUAL: 'MONTHLY', ANUAL: 'YEARLY',
};
const RFC_A_FREQ: Readonly<Record<string, FrecuenciaBase>> = {
  DAILY: 'DIARIA', WEEKLY: 'SEMANAL', MONTHLY: 'MENSUAL', YEARLY: 'ANUAL',
};

/** Construye `{frecuenciaEtiqueta, rrule}` — lo que ya espera `DatosLinea`/`CambiosLinea`. */
export function serializarRecurrencia(estado: EstadoRecurrencia): { frecuenciaEtiqueta: string; rrule: string | null } {
  if (estado.freq === 'S.N.') return { frecuenciaEtiqueta: 'SEGÚN NECESIDAD', rrule: null };

  const partes = [`FREQ=${FREQ_A_RFC[estado.freq]}`];
  if (estado.intervalo > 1) partes.push(`INTERVAL=${String(estado.intervalo)}`);

  if (estado.freq === 'SEMANAL' && estado.byday.length > 0) {
    const orden: readonly DiaSemana[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
    const dias = orden.filter((d) => estado.byday.includes(d));
    partes.push(`BYDAY=${dias.join(',')}`);
  }
  if (estado.freq === 'MENSUAL' && estado.modoMensual === 'POSICION') {
    partes.push(`BYDAY=${estado.diaSemanaMensual}`);
    partes.push(`BYSETPOS=${String(estado.posicionMensual)}`);
  }

  return { frecuenciaEtiqueta: estado.freq, rrule: partes.join(';') };
}

/**
 * La operación inversa, para precargar el formulario al editar una línea
 * ya guardada. Tolerante: si la RRULE no encaja con lo que este editor
 * sabe construir (p. ej. viene de un import con una combinación rara),
 * cae a un estado razonable en vez de reventar — el usuario sigue viendo
 * el formulario, sólo que sin todo precargado.
 */
export function analizarRecurrencia(frecuenciaEtiqueta: string, rrule: string | null): EstadoRecurrencia {
  if (rrule === null || frecuenciaEtiqueta.trim().toUpperCase() === 'SEGÚN NECESIDAD') {
    return { ...ESTADO_INICIAL, freq: 'S.N.' };
  }

  const partes = new Map<string, string>();
  for (const p of rrule.split(';')) {
    const igual = p.indexOf('=');
    if (igual < 0) continue;
    partes.set(p.slice(0, igual).trim().toUpperCase(), p.slice(igual + 1).trim().toUpperCase());
  }

  const freq = RFC_A_FREQ[partes.get('FREQ') ?? ''] ?? 'SEMANAL';
  const intervalo = Number(partes.get('INTERVAL') ?? '1') || 1;
  const bydayCrudo = partes.get('BYDAY');
  const byday = (bydayCrudo?.split(',').filter((d): d is DiaSemana => DIAS_SEMANA.some((x) => x.codigo === d)) ?? []) as DiaSemana[];
  const bysetpos = partes.get('BYSETPOS');

  if (freq === 'MENSUAL' && bysetpos !== undefined && byday.length === 1) {
    return {
      ...ESTADO_INICIAL,
      freq,
      intervalo,
      modoMensual: 'POSICION',
      posicionMensual: Number(bysetpos) || 1,
      diaSemanaMensual: byday[0]!,
    };
  }

  return { ...ESTADO_INICIAL, freq, intervalo, byday, modoMensual: 'DIA' };
}

/** Resumen legible — nunca se guarda, sólo se enseña bajo el formulario. */
export function resumenHumano(estado: EstadoRecurrencia): string {
  if (estado.freq === 'S.N.') return 'Sin recurrencia — se planifica cuando haga falta.';

  const cada = (unidad: string): string => (estado.intervalo === 1 ? `Cada ${unidad}` : `Cada ${String(estado.intervalo)} ${unidad}s`);

  if (estado.freq === 'DIARIA') return `${cada('día')}.`;
  if (estado.freq === 'ANUAL') return `${cada('año')}.`;

  if (estado.freq === 'SEMANAL') {
    if (estado.byday.length === 0) return `${cada('semana')}, el mismo día de la fecha de inicio.`;
    const dias = DIAS_SEMANA.filter((d) => estado.byday.includes(d.codigo)).map((d) => d.larga.toLowerCase());
    const lista = dias.length <= 1 ? dias.join('') : `${dias.slice(0, -1).join(', ')} y ${dias[dias.length - 1]}`;
    return `${cada('semana')}, ${lista}.`;
  }

  // MENSUAL
  if (estado.modoMensual === 'DIA') return `${cada('mes')}, el mismo día del mes que la fecha de inicio.`;
  const posicion = POSICIONES_MENSUAL.find((p) => p.valor === estado.posicionMensual)?.etiqueta ?? 'Primer';
  const dia = DIAS_SEMANA.find((d) => d.codigo === estado.diaSemanaMensual)?.larga ?? 'Lunes';
  return `${posicion} ${dia.toLowerCase()} de ${estado.intervalo === 1 ? 'cada mes' : `cada ${String(estado.intervalo)} meses`}.`;
}
